import type { Region } from "wavesurfer.js/dist/plugins/regions.js"

import { formatSeekTime } from "@/lib/audio-player-utils"

/**
 * The name a segment is announced by, with the span it covers to the
 * millisecond, so short segments within the same second stay distinct.
 */
function audioRegionAccessibleName(region: {
  start: number
  end: number
}): string {
  return region.end > region.start
    ? `Segment ${formatSeekTime(region.start)} to ${formatSeekTime(region.end)}`
    : `Marker at ${formatSeekTime(region.start)}`
}

/**
 * Puts each editable segment in the tab order as a button that opens its
 * editing menu on Enter or Space. Segments render inside the waveform's
 * shadow root, which the app's focus-ring stylesheet cannot reach, so the
 * ring is drawn inline from the same token.
 *
 * @returns Removes the listeners it added.
 */
export function makeAudioRegionsKeyboardOperable(
  regions: readonly Region[],
  onActivate: (region: Region, element: HTMLElement) => void,
): () => void {
  const cleanups = regions.flatMap((region) => {
    const element = region.element
    if (!element) {
      return []
    }
    element.tabIndex = 0
    element.setAttribute("role", "button")
    element.setAttribute("aria-label", audioRegionAccessibleName(region))

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target !== element) {
        return
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        event.stopPropagation()
        onActivate(region, element)
      }
    }
    const handleFocus = () => {
      if (element.matches(":focus-visible")) {
        element.style.outline = "1px solid var(--coval-focus-ring)"
        element.style.outlineOffset = "1px"
      }
    }
    const handleBlur = () => {
      element.style.outline = ""
      element.style.outlineOffset = ""
    }
    element.addEventListener("keydown", handleKeyDown)
    element.addEventListener("focus", handleFocus)
    element.addEventListener("blur", handleBlur)
    return [
      () => {
        element.removeEventListener("keydown", handleKeyDown)
        element.removeEventListener("focus", handleFocus)
        element.removeEventListener("blur", handleBlur)
      },
    ]
  })
  return () => {
    for (const cleanup of cleanups) {
      cleanup()
    }
  }
}
